import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import Layout from '@/components/Layout';
import UnsplashImagePicker from '@/components/UnsplashImagePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined area options
const PREDEFINED_AREAS = [
  { slug: 'mental-health', title: 'Mental Health', icon: '🧠' },
  { slug: 'sports-medicine', title: 'Sports Medicine', icon: '⚡' },
  { slug: 'reproductive-health', title: 'Reproductive Health', icon: '🌸' },
  { slug: 'pain-management', title: 'Pain Management', icon: '💪' },
  { slug: 'general-care', title: 'General Care', icon: '🏥' },
  { slug: 'nutrition-guidance', title: 'Nutrition Guidance', icon: '🥗' }
];

export default function AreaCreatePage() {
  const navigate = useNavigate();
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [useCustomSlug, setUseCustomSlug] = useState(false);
  
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    subtitle: '',
    description: '',
    additional_details: '',
    order: 0,
    enabled: true,
    image_1_link: '',
    image_2_link: '',
    image_1_url: '',
    image_2_url: '',
    icon_image_url: '' // Main icon image for homepage
  });

  const handlePredefinedSelect = (area) => {
    setFormData({
      ...formData,
      slug: area.slug,
      title: area.title
    });
  };
  
  // Auto-generate slug from title
  const handleTitleChange = (title) => {
    const slug = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setFormData({
      ...formData,
      title: title,
      slug: slug
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.slug || formData.slug.trim() === '') {
      toast.error('Please select an area or create a custom area with a valid slug');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/areas`, formData, {
        withCredentials: true
      });
      toast.success('Area created successfully!');
      navigate('/admin/areas');
    } catch (error) {
      let errorMessage = 'Failed to create area';
      if (error.response?.data) {
        const data = error.response.data;
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (Array.isArray(data)) {
          errorMessage = data.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const openUnsplashPicker = (imageField) => {
    setCurrentImageField(imageField);
    setShowUnsplashPicker(true);
  };

  const handleUnsplashSelect = (imageData) => {
    if (currentImageField === 'image_1') {
      setFormData(prev => ({ ...prev, image_1_url: imageData.url }));
    } else if (currentImageField === 'image_2') {
      setFormData(prev => ({ ...prev, image_2_url: imageData.url }));
    } else if (currentImageField === 'icon_image') {
      setFormData(prev => ({ ...prev, icon_image_url: imageData.url }));
    }
    toast.success(`Image by ${imageData.photographer} selected!`);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin/areas')}
          className="retro-button-small mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Areas
        </button>

        <div className="cassette-card p-8">
          <h1 className="text-3xl retro-title mb-6">Create New Area of Focus</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Quick Select Predefined Areas */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-3">Quick Select (Optional)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {PREDEFINED_AREAS.map(area => (
                  <button
                    key={area.slug}
                    type="button"
                    onClick={() => handlePredefinedSelect(area)}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      formData.slug === area.slug
                        ? 'border-[#E8934D] bg-[#F4E4C1]'
                        : 'border-[#6B4423] bg-white hover:bg-[#F4E4C1]'
                    }`}
                  >
                    <div className="text-2xl mb-1">{area.icon}</div>
                    <div className="text-sm font-semibold text-[#2C1810]">{area.title}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#6B4423] mt-2">Or enter a custom title below</p>
            </div>

            {/* Title field */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                placeholder="e.g., Mental Health"
                required
              />
              {formData.slug && (
                <p className="text-xs text-[#6B4423] mt-1">Slug (auto-generated): {formData.slug}</p>
              )}
            </div>

            {/* Main Icon Image - Shows on Homepage */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Homepage Icon Image *</label>
              <p className="text-xs text-[#6B4423] mb-2">This image will be displayed on the main page</p>
              <button
                type="button"
                onClick={() => openUnsplashPicker('icon_image')}
                className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                {formData.icon_image_url ? 'Replace Homepage Icon' : 'Browse Images from Unsplash'}
              </button>
              {formData.icon_image_url && (
                <div className="mt-3">
                  <img
                    src={formData.icon_image_url}
                    alt="Icon preview"
                    className="h-32 w-32 object-cover rounded border-2 border-[#6B4423]"
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Homepage icon image</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Display Order *</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                required
              />
              <p className="text-xs text-[#6B4423] mt-1">Order in which area appears on homepage (1, 2, 3...)</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Subtitle</label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Additional Details</label>
              <textarea
                value={formData.additional_details}
                onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
              />
            </div>

            {/* Image 1 */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 1</label>
              <button
                type="button"
                onClick={() => openUnsplashPicker('image_1')}
                className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                {formData.image_1_url ? 'Replace Image 1 from Unsplash' : 'Browse Images from Unsplash'}
              </button>
              {formData.image_1_url && (
                <div className="mt-3">
                  <img
                    src={formData.image_1_url}
                    alt="Image 1 preview"
                    className="h-32 w-48 object-cover rounded border-2 border-[#6B4423]"
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Selected image 1</p>
                </div>
              )}
            </div>

            {/* Image 1 Link */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 1 Link (Optional)</label>
              <input
                type="url"
                value={formData.image_1_link}
                onChange={(e) => setFormData({ ...formData, image_1_link: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                placeholder="https://example.com"
              />
              <p className="text-xs text-[#6B4423] mt-1">When visitors click on Image 1, they'll be taken to this URL</p>
            </div>

            {/* Image 2 */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 2</label>
              <button
                type="button"
                onClick={() => openUnsplashPicker('image_2')}
                className="retro-button bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                {formData.image_2_url ? 'Replace Image 2 from Unsplash' : 'Browse Images from Unsplash'}
              </button>
              {formData.image_2_url && (
                <div className="mt-3">
                  <img
                    src={formData.image_2_url}
                    alt="Image 2 preview"
                    className="h-32 w-48 object-cover rounded border-2 border-[#6B4423]"
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Selected image 2</p>
                </div>
              )}
            </div>

            {/* Image 2 Link */}
            <div>
              <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 2 Link (Optional)</label>
              <input
                type="url"
                value={formData.image_2_link}
                onChange={(e) => setFormData({ ...formData, image_2_link: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                placeholder="https://example.com"
              />
              <p className="text-xs text-[#6B4423] mt-1">When visitors click on Image 2, they'll be taken to this URL</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-5 h-5"
              />
              <label htmlFor="enabled" className="text-sm font-semibold text-[#6B4423]">
                Enable (Show on website)
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="retro-button flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Create Area
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/areas')}
                className="retro-button bg-gray-500 hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Unsplash Image Picker Modal */}
        <UnsplashImagePicker
          isOpen={showUnsplashPicker}
          onClose={() => {
            setShowUnsplashPicker(false);
            setCurrentImageField(null);
          }}
          onSelect={handleUnsplashSelect}
        />
      </div>
    </Layout>
  );
}
